import React, { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';

import { MfeVersionManagerInstanceType } from './types';

const modalStyles: React.CSSProperties = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: 'white',
  padding: '16px',
  zIndex: 10999,
  border: '1px solid #ccc',
  borderRadius: '2px',
  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
  fontFamily: 'monospace',
  width: '900px'
};

const inputStyles: React.CSSProperties = {
  width: '100%',
  padding: '8px 8px 8px 20px',
  margin: '8px 0',
  boxSizing: 'border-box'
};

const overrideFlagStyles: React.CSSProperties = {
  width: '8px',
  height: '8px',
  display: 'block',
  position: 'absolute',
  top: '22px',
  left: '8px',
  backgroundColor: '#a22',
  borderRadius: '100%'
};

const buttonStyles: React.CSSProperties = {
  padding: '10px 15px',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  marginRight: '10px'
};

interface MfeManagerUiProps {
  versionManagerInstance: MfeVersionManagerInstanceType;
  isOpen: boolean;
  onClose: () => void;
}

export const MfeVersionManagerUi: React.FC<MfeManagerUiProps> = ({
  versionManagerInstance,
  isOpen,
  onClose
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  // eslint-disable-next-line no-console
  const log = console.log;

  const versionManager = versionManagerInstance;

  const [versions, setVersions] = useState<Record<string, string>>({});
  const [serviceUrls, setServiceUrls] = useState<Record<string, string>>({});
  const [isVersionOverridden, setIsVersionOverridden] = useState<
    Record<string, boolean>
  >({});
  const [isUrlOverridden, setIsUrlOverridden] = useState<
    Record<string, boolean>
  >({});
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [urlInputValues, setUrlInputValues] = useState<Record<string, string>>(
    {}
  );
  const [needsRefresh, setNeedsRefresh] = useState<boolean>(false);

  const loadData = () => {
    versionManager.refresh();
    const currentVersions: Record<string, string> = {};
    const currentUrls: Record<string, string> = {};
    const versionOverrides: Record<string, boolean> = {};
    const urlOverrides: Record<string, boolean> = {};
    const currentInputs: Record<string, string> = {};
    const currentUrlInputs: Record<string, string> = {};

    versionManager.getMfeNames().forEach((mfeName) => {
      const version = versionManager.getVersion(mfeName);
      const serviceUrl = versionManager.getServiceBaseUrl(mfeName);
      currentVersions[mfeName] = version;
      currentUrls[mfeName] = serviceUrl;
      versionOverrides[mfeName] = versionManager.isVersionOverridden(mfeName);
      urlOverrides[mfeName] =
        versionManager.isServiceBaseUrlOverridden(mfeName);
      currentInputs[mfeName] = version;
      currentUrlInputs[mfeName] = serviceUrl;
    });
    setVersions(currentVersions);
    setServiceUrls(currentUrls);
    setIsVersionOverridden(versionOverrides);
    setIsUrlOverridden(urlOverrides);
    setInputValues(currentInputs);
    setUrlInputValues(currentUrlInputs);
  };

  useEffect(() => {
    if (isOpen) {
      loadData();
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
    // eslint-disable-next-line
  }, [isOpen]);

  const handleSave = (mfeName: string) => {
    const newVersion = inputValues[mfeName];
    const newUrl = urlInputValues[mfeName];
    let somethingChanged = false;

    if (newVersion !== versions[mfeName]) {
      versionManager.setVersionOverride(mfeName, newVersion);
      somethingChanged = true;
    }

    if (newUrl !== serviceUrls[mfeName]) {
      versionManager.setServiceBaseUrlOverride(mfeName, newUrl);
      somethingChanged = true;
    }

    if (somethingChanged) {
      log(
        `Settings for ${mfeName} saved. Please refresh the page to see the changes to ${mfeName}.`
      );
      setNeedsRefresh(true);
      loadData();
    }
  };

  const handleReset = (mfeName: string) => {
    versionManager.setVersionOverride(mfeName, null);
    versionManager.setServiceBaseUrlOverride(mfeName, null);

    log(
      `Settings for ${mfeName} have been reset. Please refresh the page to see the changes.`
    );
    setNeedsRefresh(true);
    loadData();
  };

  const handleInputChange = (mfeName: string, value: string) => {
    setInputValues((prev) => ({ ...prev, [mfeName]: value }));
  };

  const handleUrlInputChange = (mfeName: string, value: string) => {
    setUrlInputValues((prev) => ({ ...prev, [mfeName]: value }));
  };

  return (
    <dialog ref={dialogRef} style={modalStyles} onClose={onClose}>
      <h2 style={{ marginTop: 0, marginBottom: '1rem' }}>
        MFE Version Manager
      </h2>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #ccc' }}>
            <th style={{ textAlign: 'left', padding: '8px', width: '120px' }}>
              MFE Name
            </th>
            <th style={{ textAlign: 'left', padding: '8px' }}>Version</th>
            <th style={{ textAlign: 'left', padding: '8px' }}>
              Service URL Template
            </th>
            <th style={{ width: '150px', padding: '8px' }}></th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(versions).map((mfeName) => (
            <tr key={mfeName}>
              <td style={{ textTransform: 'capitalize', padding: '8px' }}>
                {mfeName}
              </td>
              <td>
                <div style={{ position: 'relative' }}>
                  {isVersionOverridden[mfeName] && (
                    <div style={overrideFlagStyles} title="Overridden"></div>
                  )}
                  <input
                    type="text"
                    style={inputStyles}
                    value={inputValues[mfeName] || ''}
                    onChange={(e) => handleInputChange(mfeName, e.target.value)}
                    placeholder="Override Version"
                  />
                </div>
              </td>
              <td>
                <div style={{ position: 'relative' }}>
                  {isUrlOverridden[mfeName] && (
                    <div style={overrideFlagStyles} title="Overridden"></div>
                  )}
                  <input
                    type="text"
                    style={inputStyles}
                    value={urlInputValues[mfeName] || ''}
                    onChange={(e) =>
                      handleUrlInputChange(mfeName, e.target.value)
                    }
                    placeholder="Override Service URL"
                  />
                </div>
              </td>
              <td style={{ textAlign: 'right', padding: '0 8px' }}>
                <button
                  style={{ ...buttonStyles, marginRight: '5px' }}
                  onClick={() => handleSave(mfeName)}
                >
                  Save
                </button>
                <button
                  style={{
                    ...buttonStyles,
                    backgroundColor: '#dc3545',
                    color: 'white',
                    marginRight: 0
                  }}
                  onClick={() => handleReset(mfeName)}
                >
                  Reset
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '1.5rem'
        }}
      >
        {needsRefresh && (
          <div
            style={{
              verticalAlign: 'middle',
              display: 'inline-block',
              padding: '8px 8px'
            }}
          >
            Changes require a{' '}
            <button
              onClick={(e) => {
                e.preventDefault();
                window.location.reload();
              }}
            >
              page refresh{' '}
            </button>
          </div>
        )}

        <button
          style={{
            ...buttonStyles,
            backgroundColor: '#6c757d',
            color: 'white'
          }}
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </dialog>
  );
};

/**
 * Injects the MFE Manager modal into the DOM and provides a global function
 * to open it from the browser console.
 *
 * @param customerConfig The customer configuration object.
 */
export function enableMfeManagerUi(
  versionManagerInstance: MfeVersionManagerInstanceType
) {
  const MfeManagerWrapper = () => {
    const [isOpen, setIsOpen] = useState(true);

    return (
      <MfeVersionManagerUi
        versionManagerInstance={versionManagerInstance}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    );
  };

  (window as any).openMfeManager = () => {
    const container = window.document.createElement('div');
    container.id = 'mfe-manager-container';
    window.document.body.appendChild(container);
    createRoot(container).render(<MfeManagerWrapper />);
  };
}
